/**
 * The manage dialog's wiring: the trainer's tags with their counts, which row is being renamed and
 * what saving it would do, and the delete prompt (exercise-library R23).
 *
 * Decides nothing. `model/exerciseTags` reads a rename before it is saved — a merge onto a name
 * the trainer already uses is named, with how many movements the merged tag will be on — and
 * `api/exerciseTagRequests` makes the call and drops what it changed.
 *
 * The list is read only while the dialog is open, and through the skeleton gate, so a fast answer
 * draws no bars and a slow one does not flicker.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExerciseListItem } from '~/entities/exercise-library';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';

import {
  deleteExerciseTag,
  renameExerciseTag,
  tagRefusalOf,
  tagUsageQueryOptions,
} from '../api/exerciseTagRequests';
import {
  MAX_TAGS_PER_TRAINER,
  orderedTagUsage,
  tagFocusedAfterRemoving,
  tagRenamePlan,
  type TagRename,
  type TagUsage,
} from '../model/exerciseTags';

/** What the dialog says after a write that did not go through. */
export type TagManagerNotice = 'invalid' | 'failed';

export interface TagManagerState {
  readonly status: 'loading' | 'error' | 'ready';
  readonly gate: SkeletonGate;
  readonly tags: readonly TagUsage[];
  readonly max: number;
  readonly retry: () => void;
  /** The row being renamed, its text, and what saving it would do. */
  readonly editing: { readonly label: string; readonly text: string; readonly plan: TagRename } | null;
  readonly startRename: (label: string) => void;
  readonly setRenameText: (text: string) => void;
  readonly cancelRename: () => void;
  readonly saveRename: () => void;
  /** The tag the delete prompt is asking about, or `null`. */
  readonly deleting: TagUsage | null;
  readonly askDelete: (tag: TagUsage) => void;
  readonly cancelDelete: () => void;
  readonly confirmDelete: () => void;
  readonly busy: boolean;
  readonly notice: TagManagerNotice | null;
  /**
   * The row whose pencil takes focus once a rename row closes — the row itself after a cancel, the
   * renamed (or merged) row after a save — and the call that says it has. The field that had
   * focus is gone by then, and focus would otherwise stay on the dialog's popup.
   */
  readonly returnFocusTo: string | null;
  readonly returnedFocus: () => void;
  /** The row whose trash takes focus after a delete, or `null` for the dialog's close. */
  readonly focusAfterDelete: string | null;
}

/**
 * @param holdFocus Called just before a rename row closes after a save. The row's field or button
 *   has focus and is about to unmount; Base UI's dialog answers focus falling to `<body>` by putting
 *   it on its popup a frame later, which lands AFTER the pencil has taken it. Handing focus to an
 *   element that survives the swap first means focus never falls, and the pencil keeps it.
 */
export function useTagManager(
  open: boolean,
  items: readonly ExerciseListItem[],
  holdFocus: () => void,
): TagManagerState {
  const client = useQueryClient();
  const { i18n } = useTranslation();
  const read = useQuery({ ...tagUsageQueryOptions(), enabled: open });
  const gate = useSkeletonGate(read.isPending);
  const tags = orderedTagUsage(read.data ?? [], i18n.language);

  const [editing, setEditing] = useState<{ label: string; text: string } | null>(null);
  const [deleting, setDeleting] = useState<TagUsage | null>(null);
  const [notice, setNotice] = useState<TagManagerNotice | null>(null);
  const [returnFocusTo, setReturnFocusTo] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: (command: { from: string; to: string }) =>
      renameExerciseTag(client, command.from, command.to),
    onSuccess: (_, command) => {
      holdFocus();
      setEditing(null);
      setReturnFocusTo(command.to);
    },
    onError: (error) => {
      setNotice(tagRefusalOf(error) === 'invalid' ? 'invalid' : 'failed');
    },
  });
  const remove = useMutation({
    mutationFn: (label: string) => deleteExerciseTag(client, label),
    onSettled: () => {
      setDeleting(null);
    },
    onError: () => {
      setNotice('failed');
    },
  });
  const busy = rename.isPending || remove.isPending;

  const plan =
    editing === null ? null : tagRenamePlan(editing.label, editing.text, tags, items);

  return {
    status: gate.waiting ? 'loading' : read.isError ? 'error' : 'ready',
    gate,
    tags,
    max: MAX_TAGS_PER_TRAINER,
    retry: () => {
      void read.refetch();
    },
    editing: editing === null || plan === null ? null : { ...editing, plan },
    startRename: (label) => {
      setNotice(null);
      setEditing({ label, text: label });
    },
    setRenameText: (text) => {
      setNotice(null);
      setEditing((current) => (current === null ? null : { ...current, text }));
    },
    cancelRename: () => {
      if (editing !== null) setReturnFocusTo(editing.label);
      setEditing(null);
      setNotice(null);
    },
    saveRename: () => {
      if (busy || editing === null || plan === null) return;
      if (plan.kind === 'unchanged') {
        setEditing(null);
        setReturnFocusTo(editing.label);
        return;
      }
      if (plan.kind === 'invalid') {
        setNotice('invalid');
        return;
      }
      rename.mutate({ from: editing.label, to: plan.label });
    },
    deleting,
    askDelete: (tag) => {
      setNotice(null);
      setDeleting(tag);
    },
    cancelDelete: () => {
      if (!remove.isPending) setDeleting(null);
    },
    confirmDelete: () => {
      if (deleting !== null && !busy) remove.mutate(deleting.label);
    },
    busy,
    notice,
    returnFocusTo,
    returnedFocus: () => {
      setReturnFocusTo(null);
    },
    focusAfterDelete:
      deleting === null
        ? null
        : tagFocusedAfterRemoving(
            tags.map((tag) => tag.label),
            deleting.label,
          ),
  };
}
